import { faFacebookSquare } from "@fortawesome/free-brands-svg-icons";
import {
  faClock,
  faEnvelope,
  faEnvelopeOpen,
  faMapMarkerAlt,
  faPhone,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../css/JuanScope/VerifyEmail.css";
import JuanEMSLogo from "../../images/JuanEMSlogo.png";
import SJDEFILogo from "../../images/SJDEFILogo.png";

// Constants
const OTP_EXPIRY_TIME = 180; // 3 minutes in seconds
const LOCKOUT_TIME = 300; // 5 minutes in seconds
const MAX_ATTEMPTS = 3;

/**
 * OTP Input component
 */
const OtpInput = ({ otp, handleChange, handleKeyDown, handlePaste, disabled, inputRefs }) => (
  <div className="juan-otp-inputs" onPaste={handlePaste}>
    {otp.map((digit, index) => (
      <input
        key={index}
        type="text"
        maxLength="1"
        value={digit}
        onChange={(e) => handleChange(index, e.target.value)}
        onKeyDown={(e) => handleKeyDown(index, e)}
        ref={(el) => (inputRefs.current[index] = el)}
        className="juan-otp-input"
        disabled={disabled}
        autoFocus={index === 0}
      />
    ))}
  </div>
);

/**
 * Header component
 */
const Header = ({ logo, altText }) => (
  <header className="juan-register-header">
    <div className="juan-header-left">
      <img src={logo} alt={altText} className="juan-logo-register" />
      <div className="juan-header-text">
        <h1>JUAN SCOPE</h1>
      </div>
    </div>
  </header>
);

/**
 * Footer component
 */
const Footer = () => (
  <footer className="juan-register-footer">
    <div className="juan-footer-left">
      <img src={JuanEMSLogo} alt="JuanEMS Logo" className="juan-footer-logo" />
      <div className="juan-footer-text">
        <h1>JuanEMS - JUAN SCOPE</h1>
        <p className="juan-footer-motto">2025. San Juan De Dios Educational Foundation Inc.</p>
      </div>
    </div>
    <div className="juan-footer-content">
      <div className="juan-footer-links">
        <a href="/about" className="footer-link">About</a>
        <span className="footer-link-separator"> | </span>
        <a href="/terms-of-use" className="footer-link">Terms of Use</a>
        <span className="footer-link-separator"> | </span>
        <a href="/privacy" className="footer-link">Privacy</a>
      </div>
      <a
        href="https://www.facebook.com/SJDEFIcollege"
        target="_blank"
        rel="noopener noreferrer"
        className="juan-footer-social-link"
      >
        <FontAwesomeIcon icon={faFacebookSquare} className="juan-social-icon" />
        <div className="juan-social-text">
          <span className="juan-social-find">Find us on</span>
          <span className="juan-social-platform">Facebook</span>
        </div>
      </a>
      <div className="juan-footer-contact-container">
        <div className="juan-contact-title">
          <FontAwesomeIcon icon={faPhone} />
          <span>CONTACT US</span>
        </div>
        <div className="juan-contact-items">
          <div className="juan-contact-item">
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            <span>2772 Roxas Blvd., Pasay City, Philippines, 1300</span>
          </div>
          <div className="juan-contact-item">
            <FontAwesomeIcon icon={faPhone} />
            <span>+632 551-2763</span>
          </div>
          <div className="juan-contact-item">
            <FontAwesomeIcon icon={faEnvelope} />
            <span>admission_office@sjdefi.edu.ph | registrarsoffice@sjdefi.edu.ph</span>
          </div>
          <div className="juan-contact-item">
            <FontAwesomeIcon icon={faClock} />
            <span>Monday to Thursday - 7:00 AM to 5:00 PM | Friday - 7:00 AM to 4:00 PM</span>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

/**
 * Main component for OTP verification during registration
 */
function VerifyOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputsRef = useRef([]);
  const timerRef = useRef(null);
  const lockoutTimerRef = useRef(null);

  // State variables
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [lockoutCountdown, setLockoutCountdown] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);

  // Get data from location state
  const email = location.state?.email || "";
  const firstName = location.state?.firstName || "";
  const lastName = location.state?.lastName || "";
  const registrationData = location.state?.registrationData || null;

  // Local storage key for OTP verification
  const storageKey = `otpVerification_signup_${email}`;

  // Function to save verification state to localStorage
  const saveVerificationState = (state) => {
    const verificationState = {
      email,
      otpExpiry: state.otpExpiry || Date.now() + otpCountdown * 1000,
      lockoutExpiry: state.lockoutExpiry ||
        (isLockedOut ? Date.now() + lockoutCountdown * 1000 : 0),
      attemptsLeft: state.attemptsLeft !== undefined ? state.attemptsLeft : attemptsLeft,
      isLockedOut: state.isLockedOut !== undefined ? state.isLockedOut : isLockedOut,
      firstName,
      lastName,
    };
    localStorage.setItem(storageKey, JSON.stringify(verificationState));
  };

  // Function to load verification state from localStorage
  const loadVerificationState = () => {
    const storedState = localStorage.getItem(storageKey);
    if (!storedState) return null;

    try {
      const parsedState = JSON.parse(storedState);
      if (parsedState.email !== email) return null;

      const now = Date.now();
      const otpTimeLeft = parsedState.otpExpiry
        ? Math.max(0, Math.floor((parsedState.otpExpiry - now) / 1000))
        : 0;
      const lockoutTimeLeft = parsedState.lockoutExpiry
        ? Math.max(0, Math.floor((parsedState.lockoutExpiry - now) / 1000))
        : 0;

      return {
        otpTimeLeft,
        lockoutTimeLeft,
        attemptsLeft: parsedState.attemptsLeft,
        isLockedOut: parsedState.lockoutExpiry > now,
      };
    } catch (error) {
      console.error("Error parsing verification state:", error);
      return null;
    }
  };

  // Format time from seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Initialize state from localStorage or set default values
  useEffect(() => {
    if (!email) {
      navigate("/register");
      return;
    }

    const localState = loadVerificationState();

    if (localState) {
      setOtpCountdown(localState.otpTimeLeft);
      setLockoutCountdown(localState.lockoutTimeLeft);
      setAttemptsLeft(localState.attemptsLeft);
      setIsLockedOut(localState.isLockedOut);
      setCanResend(localState.otpTimeLeft <= 0 && !localState.isLockedOut);
    } else {
      setOtpCountdown(OTP_EXPIRY_TIME);
      setCanResend(false);
      saveVerificationState({
        otpExpiry: Date.now() + OTP_EXPIRY_TIME * 1000,
        lockoutExpiry: 0,
        attemptsLeft: MAX_ATTEMPTS,
        isLockedOut: false,
      });
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
    };
  }, [email, navigate]);

  // OTP countdown timer
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!isLockedOut && otpCountdown > 0) {
      timerRef.current = setInterval(() => {
        setOtpCountdown((prevCount) => {
          const newCount = prevCount - 1;
          if (newCount <= 0) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setCanResend(true);
            saveVerificationState({
              otpExpiry: 0,
              attemptsLeft: attemptsLeft,
            });
            return 0;
          }
          return newCount;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLockedOut, otpCountdown, attemptsLeft]);

  // Lockout countdown timer
  useEffect(() => {
    if (lockoutTimerRef.current) {
      clearInterval(lockoutTimerRef.current);
      lockoutTimerRef.current = null;
    }

    if (isLockedOut && lockoutCountdown > 0) {
      lockoutTimerRef.current = setInterval(() => {
        setLockoutCountdown((prevCount) => {
          const newCount = prevCount - 1;
          if (newCount <= 0) {
            clearInterval(lockoutTimerRef.current);
            lockoutTimerRef.current = null;
            setIsLockedOut(false);
            setCanResend(true);
            saveVerificationState({
              lockoutExpiry: 0,
              isLockedOut: false,
            });
            return 0;
          }
          return newCount;
        });
      }, 1000);
    }

    return () => {
      if (lockoutTimerRef.current) {
        clearInterval(lockoutTimerRef.current);
        lockoutTimerRef.current = null;
      }
    };
  }, [isLockedOut, lockoutCountdown]);

  // Handle OTP input change
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (error) setError("");

    // Auto-focus next input
    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  // Handle backspace key press
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  // Handle paste event for OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      inputsRef.current[5].focus();
    }
  };

  // Handle submit - verify OTP and create account
  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    setSuccess("");
    setError("");

    try {
      console.log("Verifying OTP with data:", { email, otp: otpString });

      const verifyResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/verify-signup-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            otp: otpString,
          }),
        }
      );

      const verifyData = await verifyResponse.json();
      console.log("OTP Verification Response:", verifyData);

      if (!verifyResponse.ok) {
        // Decrement attempts counter
        const newAttemptsLeft = attemptsLeft - 1;
        setAttemptsLeft(newAttemptsLeft);

        // Check if we need to lock the user out
        if (newAttemptsLeft <= 0) {
          setIsLockedOut(true);
          setLockoutCountdown(LOCKOUT_TIME);
          setCanResend(false);

          // Save lockout state
          saveVerificationState({
            attemptsLeft: 0,
            isLockedOut: true,
            lockoutExpiry: Date.now() + LOCKOUT_TIME * 1000,
          });

          throw new Error(
            "Too many failed attempts. Please try again after the lockout period."
          );
        } else {
          // Just update attempts
          saveVerificationState({ attemptsLeft: newAttemptsLeft });
          throw new Error(verifyData.error || "Invalid OTP code");
        }
      }

      // OTP is verified successfully, now create the account
      if (!registrationData) {
        throw new Error("Registration data not found");
      }

      console.log("Creating account with registration data:", registrationData);
      const registrationResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/signup-applicant`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(registrationData),
        }
      );

      const registrationData2 = await registrationResponse.json();
      console.log("Registration Response:", registrationData2);

      if (!registrationResponse.ok) {
        throw new Error(registrationData2.error || "Failed to create account");
      }

      // Account created successfully - store user data
      const userData = {
        userEmail: registrationData2.email,
        firstName: registrationData2.firstName,
        studentID: registrationData2.studentID,
        applicantID: registrationData2.applicantID,
        lastLogin: registrationData2.lastLogin,
        lastLogout: registrationData2.lastLogout,
        createdAt: registrationData2.createdAt,
        activityStatus: registrationData2.activityStatus,
        loginAttempts: "0"
      };

      // Store user data in localStorage
      Object.entries(userData).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      // Clear OTP verification data
      localStorage.removeItem(storageKey);

      // Show success message and toast notification about credentials
      setSuccess(
        registrationData2.message ||
        "Account created successfully! Redirecting to dashboard..."
      );

      // Display toast notification about credentials sent to email
      toast.success(
        "Your login credentials have been sent to your email address. Please check your inbox to proceed with login.",
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Navigate to dashboard after delay
      setTimeout(() => {
        navigate("/scope-dashboard");
      }, 5000);
    } catch (err) {
      console.error("OTP verification error:", err);
      setError(err.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResend = async () => {
    if (!canResend || isLockedOut) return;

    setResendLoading(true);
    setError("");
    setSuccess("");

    try {
      console.log("Resending OTP to:", email);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/enrollee-applicants/send-signup-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            firstName,
            lastName,
          }),
        }
      );

      const data = await response.json();
      console.log("Resend OTP Response:", data);

      if (data.success) {
        // Reset OTP state
        setOtpCountdown(OTP_EXPIRY_TIME);
        setCanResend(false);
        setAttemptsLeft(MAX_ATTEMPTS);
        setSuccess("New verification code sent to your email");

        // Clear OTP inputs
        setOtp(["", "", "", "", "", ""]);
        inputsRef.current[0].focus();

        // Save new state to localStorage
        saveVerificationState({
          otpExpiry: Date.now() + OTP_EXPIRY_TIME * 1000,
          attemptsLeft: MAX_ATTEMPTS,
          isLockedOut: false,
          lockoutExpiry: 0,
        });

        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError(data.message || "Failed to resend verification code");
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setError("Failed to resend verification code. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="juan-verify-container">
      <ToastContainer />
      <Header logo={SJDEFILogo} altText="SJDEFI Logo" />

      <div className="juan-verify-main">
        <div className="juan-verify-card">
          <FontAwesomeIcon icon={faEnvelopeOpen} size="3x" className="juan-verify-icon" />
          <h2>Verify Your Registration</h2>
          <p className="juan-verify-description">
            Hi {firstName}! Please enter the 6-digit verification code sent to {email} to verify your account.
          </p>

          <form onSubmit={handleSubmit} className="juan-otp-form">
            <OtpInput
              otp={otp}
              handleChange={handleChange}
              handleKeyDown={handleKeyDown}
              handlePaste={handlePaste}
              disabled={loading || isLockedOut}
              inputRefs={inputsRef}
            />

            {attemptsLeft < MAX_ATTEMPTS && !isLockedOut && (
              <p className="juan-otp-attempts">Attempts remaining: {attemptsLeft}</p>
            )}

            {otpCountdown > 0 && (
              <p className="juan-otp-timer">Code expires in: {formatTime(otpCountdown)}</p>
            )}

            {isLockedOut && (
              <p className="juan-otp-error">
                Too many attempts. Please wait {formatTime(lockoutCountdown)} to try again.
              </p>
            )}

            {error && <p className="juan-otp-error">{error}</p>}
            {success && <p className="juan-otp-success">{success}</p>}

            <div className="juan-otp-actions">
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading || !canResend || isLockedOut}
                className="juan-resend-button"
              >
                {resendLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Resend Code"}
              </button>
              <button
                type="submit"
                disabled={loading || isLockedOut}
                className="juan-verify-button"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : "Verify"}
              </button>
            </div>
          </form>

          <p className="juan-verify-note">
            If you don't receive the code, check your spam folder or click Resend Code.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default VerifyOTP;